/**
 * Role — see GLOSSARY.md → Access Control → Role.
 *
 * An application access-control role governing which Protected Operations a
 * Principal may perform. The four roles are hierarchical and additive
 * (FR-007): viewer ⊂ editor ⊂ approver ⊂ admin. A Principal holding several
 * roles is reduced to its most capable one ("the most capable applies",
 * FR-008 — see `maxRoleRank`).
 *
 * DISTINCT from the clinical `Roles` (GLOSSARY → Trial Authoring — the IRT
 * user roles that execute a Test Case). See GLOSSARY Disambiguation: the two
 * are orthogonal and never overloaded.
 *
 * Pure value object (zero infra). The closed-literal-union idiom mirrors
 * generation-category.ts; shared from this package because both the api
 * authorization policy and the web UI gating consume it.
 */
export const ROLES = ['viewer', 'editor', 'approver', 'admin'] as const;
export type Role = (typeof ROLES)[number];

export const isRole = (raw: unknown): raw is Role =>
  typeof raw === 'string' && (ROLES as readonly string[]).includes(raw);

export const parseRole = (raw: unknown): Role => {
  if (!isRole(raw)) {
    throw new Error(`Invalid Role: ${JSON.stringify(raw)} (expected one of ${ROLES.join(', ')})`);
  }
  return raw;
};

// Hierarchy (FR-007): viewer ⊂ editor ⊂ approver ⊂ admin.
const ROLE_RANK: Record<Role, 0 | 1 | 2 | 3> = { viewer: 0, editor: 1, approver: 2, admin: 3 };
export const roleRank = (role: Role): 0 | 1 | 2 | 3 => ROLE_RANK[role];

/**
 * FR-008 — when several roles are held the most capable applies (max rank).
 * An empty role set yields the **-1 fail-closed sentinel** (I2): it out-ranks
 * nothing, so a zero-role Principal is denied every Protected Operation
 * (FR-018). The return type carries the sentinel so callers cannot forget it.
 */
export const maxRoleRank = (roles: readonly Role[]): -1 | 0 | 1 | 2 | 3 =>
  roles.reduce<-1 | 0 | 1 | 2 | 3>(
    (max, role) => (roleRank(role) > max ? roleRank(role) : max),
    -1,
  );

export const isAdmin = (roles: readonly Role[]): boolean => roles.includes('admin');
